import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useApp } from "@/context/AppContext";
import { fetchSiteConfig } from "@/lib/api";

const navLinks = [
    { to: "/", label: "Index" },
    { to: "/shop", label: "Catalog", hasDropdown: true },
    { to: "/research-guidelines", label: "Research" },
];

const FALLBACK_CATALOG_FILTERS = [
    { cat: "All", label: "All Products" },
    { cat: "GLP-1s", label: "GLP-1s" },
    { cat: "Healing Peptides", label: "Healing Peptides" },
    { cat: "HGH & Ancillaries", label: "HGH & Ancillaries" },
    { cat: "Lab Supplies", label: "Lab Supplies" },
];

export const Header = () => {
    const { cartCount, currency, setCurrency } = useApp();
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState(FALLBACK_CATALOG_FILTERS);

    useEffect(() => {
        fetchSiteConfig().then((c) => {
            if (Array.isArray(c?.catalog_filters) && c.catalog_filters.length) setFilters(c.catalog_filters);
        }).catch(() => {});
    }, []);

    return (
        <header className="sticky top-0 z-50 glass-dark border-b border-ink">
            {/* Top ticker */}
            <div className="bg-neon text-black overflow-hidden" data-testid="top-marquee">
                <div className="flex">
                    <div className="marquee whitespace-nowrap py-1.5 overline text-[10px] font-semibold">
                        {Array.from({length: 2}).map((_, k) => (
                            <div key={k} className="flex items-center gap-12 pr-12">
                                <span>◆ DISCREET UNMARKED SHIPPING</span>
                                <span>◆ JANOSHIK COA VERIFIED</span>
                                <span>◆ SHIPS FROM CANADA · CA + US</span>
                                <span>◆ RESEARCH GRADE · ≥99% PURITY</span>
                                <span>◆ GUEST CHECKOUT · CRYPTO</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" className="text-ink" data-testid="logo-link"><Logo /></Link>

                    <nav className="hidden md:flex items-center gap-10">
                        {navLinks.map((l) => (
                            l.hasDropdown ? (
                                <div key={l.to + l.label} className="relative group" data-testid="nav-catalog-wrapper">
                                    <NavLink
                                        to={l.to}
                                        end={l.to === "/"}
                                        className={({ isActive }) =>
                                            `flex items-center gap-1 text-xs font-mono uppercase tracking-[0.18em] font-medium transition-colors ${isActive ? "text-neon" : "text-ink-dim hover:text-ink"}`
                                        }
                                        data-testid={`nav-${l.label.toLowerCase()}`}
                                    >
                                        {l.label}
                                        <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" strokeWidth={2} />
                                    </NavLink>
                                    {/* Hover dropdown, invisible bridge keeps it open while moving cursor */}
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 z-50" data-testid="catalog-dropdown">
                                        <div className="w-72 border border-ink-strong bg-ink-base/95 backdrop-blur-md shadow-2xl">
                                            <div className="px-4 py-3 border-b border-ink overline text-[10px] text-neon">/ FILTER BY FAMILY</div>
                                            <ul className="py-1.5">
                                                {filters.map((f) => (
                                                    <li key={f.cat}>
                                                        <Link
                                                            to={f.cat === "All" ? "/shop" : `/shop?cat=${encodeURIComponent(f.cat)}`}
                                                            className={`block px-4 py-2.5 text-xs font-mono uppercase tracking-[0.12em] transition-colors hover:bg-ink-surface ${f.highlight ? "text-gold border-l-2 border-gold/60" : "text-ink-dim hover:text-ink"}`}
                                                            data-testid={`catalog-dropdown-${f.cat.replace(/\s+/g,'-').toLowerCase()}`}
                                                        >
                                                            {f.label}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <NavLink
                                    key={l.to + l.label}
                                    to={l.to}
                                    end={l.to === "/"}
                                    className={({ isActive }) =>
                                        `text-xs font-mono uppercase tracking-[0.18em] font-medium transition-colors ${isActive ? "text-neon" : "text-ink-dim hover:text-ink"}`
                                    }
                                    data-testid={`nav-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </NavLink>
                            )
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-4">
                        <div className="hidden sm:flex items-center border border-ink-strong font-mono text-[11px]" data-testid="usd-cad-toggle">
                            <button
                                onClick={() => setCurrency("USD")}
                                className={`px-3 py-1.5 font-semibold transition-colors ${currency === "USD" ? "bg-neon text-black" : "text-ink-dim hover:text-ink"}`}
                                data-testid="currency-usd-btn"
                            >USD</button>
                            <button
                                onClick={() => setCurrency("CAD")}
                                className={`px-3 py-1.5 font-semibold transition-colors ${currency === "CAD" ? "bg-neon text-black" : "text-ink-dim hover:text-ink"}`}
                                data-testid="currency-cad-btn"
                            >CAD</button>
                        </div>

                        <Link
                            to="/cart"
                            className="relative p-2 text-ink hover:text-neon transition-colors"
                            data-testid="header-cart-btn"
                            aria-label="Cart"
                        >
                            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                            {cartCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-gold text-black text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center font-mono"
                                    data-testid="cart-count">{cartCount}</span>
                            )}
                        </Link>

                        <button
                            className="md:hidden p-2 text-ink"
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-btn"
                            aria-label="Menu"
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="md:hidden border-t border-ink bg-ink-base" data-testid="mobile-menu">
                    <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-4">
                        {navLinks.map((l) => (
                            <Link
                                key={l.to + l.label}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                className="text-xs font-mono uppercase tracking-[0.18em] text-ink-dim py-1"
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                            >{l.label}</Link>
                        ))}
                        <div className="border-t border-ink-strong pt-3 mt-1">
                            <div className="overline text-[10px] text-neon mb-2">FILTER BY FAMILY</div>
                            {filters.map((f) => (
                                <Link
                                    key={f.cat}
                                    to={f.cat === "All" ? "/shop" : `/shop?cat=${encodeURIComponent(f.cat)}`}
                                    onClick={() => setOpen(false)}
                                    className={`block py-1.5 text-xs font-mono uppercase tracking-[0.12em] ${f.highlight ? "text-gold" : "text-ink-dim"}`}
                                    data-testid={`mobile-catalog-${f.cat.replace(/\s+/g,'-').toLowerCase()}`}
                                >
                                    {f.label}
                                </Link>
                            ))}
                        </div>
                        <div className="flex items-center border border-ink-strong text-xs font-mono w-fit mt-2">
                            <button onClick={() => setCurrency("USD")} className={`px-3 py-1.5 font-semibold ${currency === "USD" ? "bg-neon text-black" : "text-ink-dim"}`}>USD</button>
                            <button onClick={() => setCurrency("CAD")} className={`px-3 py-1.5 font-semibold ${currency === "CAD" ? "bg-neon text-black" : "text-ink-dim"}`}>CAD</button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};
